import React, { useState } from 'react';
import { UnifiedMap, Job, PackageData, TeamMember } from './unified-map/UnifiedMap';
import { UnifiedMapPanel } from './unified-map/UnifiedMapPanel';
import { EmergencyIntervention } from './opportunities/EmergencyHoverTooltip';
import { EmergencyDetailModal } from './opportunities/EmergencyDetailModal';
export function UnifiedMapPage() {
  // --- State ---
  const [activeTab, setActiveTab] = useState<'jobs' | 'opportunities'>('jobs');
  const [isPanelCollapsed, setIsPanelCollapsed] = useState(false);
  // Selection
  const [activeJobId, setActiveJobId] = useState<number | null>(null);
  const [activePackageId, setActivePackageId] = useState<number | null>(null);
  const [selectedMemberId, setSelectedMemberId] = useState<string | null>(null);
  const [selectedEmergencyId, setSelectedEmergencyId] = useState<number | null>(null);
  const [emergencyPosition, setEmergencyPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);
  // Visibility Layers
  const [layerVisibility, setLayerVisibility] = useState({
    jobs: true,
    opportunities: true,
    team: true
  });
  // --- Mock Data (Consolidated) ---
  const teamMembers: TeamMember[] = [{
    id: 'tm-1',
    name: 'Lucia M.',
    initials: 'LM',
    color: '#fce7f3',
    textColor: '#be185d',
    currentLat: 41.908,
    currentLng: 12.467
  }, {
    id: 'tm-2',
    name: 'Marco R.',
    initials: 'MR',
    color: '#dbeafe',
    textColor: '#1d4ed8',
    currentLat: 41.9015,
    currentLng: 12.476
  }, {
    id: 'tm-3',
    name: 'Alessandro V.',
    initials: 'AV',
    color: '#dcfce7',
    textColor: '#15803d',
    currentLat: 41.89,
    currentLng: 12.495
  }, {
    id: 'tm-4',
    name: 'Francesca N.',
    initials: 'FN',
    color: '#ffedd5',
    textColor: '#c2410c',
    currentLat: 41.9055,
    currentLng: 12.482
  }];
  const jobs: Job[] = [{
    id: 1,
    timeStart: '10:00',
    timeEnd: '12:00',
    address: 'Via Roma 4',
    city: 'Roma',
    host: 'Marco B.',
    type: 'Pulizia Standard',
    cleaner: {
      name: 'Lucia M.',
      avatarColor: 'bg-pink-100 text-pink-700',
      initials: 'LM'
    },
    cleanerId: 'tm-1',
    status: 'in-progress',
    earnings: '€50',
    lat: 41.9085,
    lng: 12.4665
  }, {
    id: 2,
    timeStart: '13:00',
    timeEnd: '14:30',
    address: 'Corso Italia 22',
    city: 'Roma',
    host: 'Giulia R.',
    type: 'Check-out',
    cleaner: {
      name: 'Marco R.',
      avatarColor: 'bg-blue-100 text-blue-700',
      initials: 'MR'
    },
    cleanerId: 'tm-2',
    status: 'scheduled',
    earnings: '€45',
    lat: 41.903,
    lng: 12.479
  }, {
    id: 3,
    timeStart: '09:00',
    timeEnd: '11:00',
    address: 'Piazza Navona 5',
    city: 'Roma',
    host: 'Roberto F.',
    type: 'Pulizia Profonda',
    cleaner: {
      name: 'Alessandro V.',
      avatarColor: 'bg-green-100 text-green-700',
      initials: 'AV'
    },
    cleanerId: 'tm-3',
    status: 'completed',
    earnings: '€80',
    lat: 41.8992,
    lng: 12.4731
  }, {
    id: 4,
    timeStart: '15:00',
    timeEnd: '17:00',
    address: 'Via del Corso 10',
    city: 'Roma',
    host: 'Elena S.',
    type: 'Pulizia Standard',
    cleaner: {
      name: 'Francesca N.',
      avatarColor: 'bg-orange-100 text-orange-700',
      initials: 'FN'
    },
    cleanerId: 'tm-4',
    status: 'issue',
    earnings: '€55',
    lat: 41.9058,
    lng: 12.4823
  }, {
    id: 5,
    timeStart: '11:30',
    timeEnd: '13:00',
    address: 'Via Condotti 8',
    city: 'Roma',
    host: 'Luigi P.',
    type: 'Pulizia Standard',
    cleaner: {
      name: 'Lucia M.',
      avatarColor: 'bg-pink-100 text-pink-700',
      initials: 'LM'
    },
    cleanerId: 'tm-1',
    status: 'completed',
    earnings: '€40',
    lat: 41.906,
    lng: 12.481
  }];
  const packages: PackageData[] = [{
    id: 1,
    name: 'Pacchetto Zona Prati',
    zoneName: 'Prati',
    lat: 41.907,
    lng: 12.4665,
    count: 3,
    baseEarnings: 140,
    status: 'available',
    properties: [{
      id: 101,
      address: 'Via Cola di Rienzo 28',
      mq: 85,
      rooms: 3,
      bathrooms: 2,
      timeWindow: '10:00 - 14:00',
      tags: ['Parquet'],
      lat: 41.9085,
      lng: 12.4665
    }, {
      id: 102,
      address: 'Via Crescenzio 12',
      mq: 60,
      rooms: 2,
      bathrooms: 1,
      timeWindow: '11:00 - 15:00',
      tags: ['Ascensore'],
      lat: 41.906,
      lng: 12.464
    }, {
      id: 103,
      address: 'Piazza Cavour 5',
      mq: 110,
      rooms: 4,
      bathrooms: 2,
      timeWindow: '09:00 - 13:00',
      tags: ['Lusso'],
      lat: 41.9065,
      lng: 12.469
    }]
  }, {
    id: 2,
    name: 'Pacchetto Centro Storico',
    zoneName: 'Centro',
    lat: 41.904,
    lng: 12.48,
    count: 2,
    baseEarnings: 110,
    status: 'available',
    properties: [{
      id: 201,
      address: 'Via del Corso 150',
      mq: 90,
      rooms: 3,
      bathrooms: 2,
      timeWindow: '10:30 - 14:30',
      tags: ['ZTL'],
      lat: 41.903,
      lng: 12.479
    }, {
      id: 202,
      address: 'Piazza di Spagna 3',
      mq: 120,
      rooms: 4,
      bathrooms: 3,
      timeWindow: '11:00 - 15:00',
      tags: ['Lusso'],
      lat: 41.9058,
      lng: 12.4823
    }]
  }, {
    id: 3,
    name: 'Pacchetto Trastevere',
    zoneName: 'Trastevere',
    lat: 41.8892,
    lng: 12.4708,
    count: 2,
    baseEarnings: 95,
    status: 'accepted',
    properties: [{
      id: 301,
      address: 'Via della Lungaretta 12',
      mq: 55,
      rooms: 2,
      bathrooms: 1,
      timeWindow: '10:00 - 13:00',
      tags: ['Scale'],
      lat: 41.8895,
      lng: 12.4715
    }, {
      id: 302,
      address: 'Piazza Santa Maria 4',
      mq: 75,
      rooms: 3,
      bathrooms: 1,
      timeWindow: '11:00 - 14:00',
      tags: ['Rumoroso'],
      lat: 41.889,
      lng: 12.47
    }]
  }];
  const emergencyInterventions: EmergencyIntervention[] = [{
    id: 901,
    lat: 41.895,
    lng: 12.485,
    address: 'Via dei Fori Imperiali 10',
    zoneName: 'Centro',
    cleaningType: 'Pulizia Emergenza Post-Allagamento',
    description: 'Allagamento bagno principale, acqua su pavimento parquet. Necessaria asciugatura e pulizia profonda.',
    mq: 95,
    rooms: 3,
    bathrooms: 2,
    floor: 3,
    tags: ['Parquet', 'Fragile', 'Urgente'],
    hostName: 'Giovanni M.',
    nextCheckIn: '16:00',
    deadlineMs: Date.now() + 2 * 60 * 60 * 1000,
    quotesReceived: 3,
    suggestedPriceMin: 85,
    suggestedPriceMax: 120
  }, {
    id: 902,
    lat: 41.912,
    lng: 12.455,
    address: 'Viale Vaticano 45',
    zoneName: 'Vaticano',
    cleaningType: 'Pulizia Profonda Post-Festa',
    description: 'Festa in appartamento, necessaria pulizia profonda di tutte le stanze e sanificazione.',
    mq: 110,
    rooms: 4,
    bathrooms: 2,
    floor: 2,
    tags: ['Sanificazione', 'Rifiuti extra'],
    hostName: 'Laura B.',
    nextCheckIn: '11:00 domani',
    deadlineMs: Date.now() + 5 * 60 * 60 * 1000,
    quotesReceived: 1,
    suggestedPriceMin: 95,
    suggestedPriceMax: 145
  }, {
    id: 903,
    lat: 41.8835,
    lng: 12.4895,
    address: 'Via Cavour 78',
    zoneName: 'Monti',
    cleaningType: 'Emergenza Rottura Tubo',
    description: 'Rottura tubo sotto il lavandino della cucina. Acqua su tutto il pavimento, necessaria asciugatura urgente e pulizia.',
    mq: 65,
    rooms: 2,
    bathrooms: 1,
    floor: 1,
    tags: ['Urgente', 'Danni acqua', 'Piano terra'],
    hostName: 'Marco R.',
    nextCheckIn: '14:00',
    deadlineMs: Date.now() + 1.5 * 60 * 60 * 1000,
    quotesReceived: 5,
    suggestedPriceMin: 70,
    suggestedPriceMax: 100
  }, {
    id: 904,
    lat: 41.8725,
    lng: 12.4802,
    address: 'Viale Aventino 32',
    zoneName: 'Aventino',
    cleaningType: 'Pulizia Urgente Check-out Anticipato',
    description: 'Ospite partito in anticipo, appartamento in condizioni pessime. Prossimo check-in tra poche ore.',
    mq: 80,
    rooms: 3,
    bathrooms: 1,
    floor: 4,
    tags: ['Check-out', 'Urgente', 'Ascensore'],
    hostName: 'Francesca L.',
    nextCheckIn: '15:00',
    deadlineMs: Date.now() + 3 * 60 * 60 * 1000,
    quotesReceived: 2,
    suggestedPriceMin: 75,
    suggestedPriceMax: 110
  }, {
    id: 905,
    lat: 41.9155,
    lng: 12.4725,
    address: 'Via Cola di Rienzo 185',
    zoneName: 'Prati',
    cleaningType: 'Sanificazione Post-Infestazione',
    description: 'Segnalata infestazione insetti. Necessaria sanificazione completa e pulizia profonda di tutti gli ambienti.',
    mq: 100,
    rooms: 4,
    bathrooms: 2,
    floor: 5,
    tags: ['Sanificazione', 'Urgente', 'Prodotti speciali'],
    hostName: 'Alessandro V.',
    nextCheckIn: '12:00 domani',
    deadlineMs: Date.now() + 4 * 60 * 60 * 1000,
    quotesReceived: 0,
    suggestedPriceMin: 110,
    suggestedPriceMax: 160
  }];
  // --- Handlers ---
  const handleToggleLayer = (layer: 'jobs' | 'opportunities' | 'team') => {
    setLayerVisibility(prev => ({
      ...prev,
      [layer]: !prev[layer]
    }));
  };
  const handleJobSelect = (id: number) => {
    setActiveJobId(id);
    setActivePackageId(null);
    setActiveTab('jobs');
    // Ensure jobs layer is visible
    if (!layerVisibility.jobs) setLayerVisibility(prev => ({
      ...prev,
      jobs: true
    }));
  };
  const handlePackageSelect = (id: number) => {
    setActivePackageId(id);
    setActiveJobId(null);
    setActiveTab('opportunities');
    // Ensure opportunities layer is visible
    if (!layerVisibility.opportunities) setLayerVisibility(prev => ({
      ...prev,
      opportunities: true
    }));
  };
  const handleMemberSelect = (id: string | null) => {
    setSelectedMemberId(id);
    // If selecting a member, ensure team layer is visible
    if (id && !layerVisibility.team) setLayerVisibility(prev => ({
      ...prev,
      team: true
    }));
  };
  const handleEmergencyClick = (id: number) => {
    setSelectedEmergencyId(id);
  };
  const selectedEmergency = emergencyInterventions.find(e => e.id === selectedEmergencyId);
  return <div className="relative h-[calc(100vh-64px)] overflow-hidden -m-4 lg:-m-8" data-id="element-2599">
      {/* Full Screen Map */}
      <div className="absolute inset-0 z-0" data-id="element-2600">
        <UnifiedMap jobs={jobs} packages={packages} teamMembers={teamMembers} emergencyInterventions={emergencyInterventions} activeJobId={activeJobId} activePackageId={activePackageId} selectedMemberId={selectedMemberId} selectedEmergencyId={selectedEmergencyId} onJobSelect={handleJobSelect} onPackageSelect={handlePackageSelect} onMemberSelect={handleMemberSelect} onEmergencyClick={handleEmergencyClick} onEmergencyPositionUpdate={setEmergencyPosition} showJobs={layerVisibility.jobs} showOpportunities={layerVisibility.opportunities} showTeam={layerVisibility.team} data-id="element-2601" />
      </div>

      {/* Floating Panel */}
      <UnifiedMapPanel jobs={jobs} packages={packages} teamMembers={teamMembers} activeTab={activeTab} onTabChange={setActiveTab} activeJobId={activeJobId} activePackageId={activePackageId} onJobSelect={handleJobSelect} onPackageSelect={handlePackageSelect} layerVisibility={layerVisibility} onToggleLayer={handleToggleLayer} isCollapsed={isPanelCollapsed} onToggleCollapse={() => setIsPanelCollapsed(!isPanelCollapsed)} data-id="element-2602" />

      {/* Emergency Modal */}
      {selectedEmergency && <EmergencyDetailModal intervention={selectedEmergency} isOpen={!!selectedEmergency} onClose={() => {
      setSelectedEmergencyId(null);
      setEmergencyPosition(null);
    }} onSubmit={() => {
      alert('Preventivo inviato!');
      setSelectedEmergencyId(null);
    }} position={emergencyPosition} data-id="element-2603" />}
    </div>;
}